import { PlatformConnectionSource, Persistence } from "@heydex/client-runtime/platform";
import {
  PrimaryConnectionRegistration,
  PrimaryConnectionTarget,
} from "@heydex/client-runtime/connection";
import { EnvironmentRpcRequestObserver } from "@heydex/client-runtime/rpc";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Schedule from "effect/Schedule";
import * as Stream from "effect/Stream";

import { resolveInitialPrimaryEnvironmentDescriptor } from "../environments/primary/context";
import { primaryHttpBaseUrl } from "../environments/primary/target";
import { clearComposerDraftsEnvironment } from "../composerDraftStore";
import { layerRuntimeConnector } from "../localRuntime";
import { acknowledgeRpcRequest, trackRpcRequestSent } from "../rpc/requestLatencyState";
import * as ConnectionStorage from "./storage";

let nextObservedRpcRequestId = 0;

// A runtime that failed to answer is restarted by `localRuntime`; the next read reaches it.
const DESCRIPTOR_RETRY = Schedule.spaced("1 second");

/** The one environment: the runtime in this page, registered once its descriptor is read. */
const layerPlatformConnectionSource = Layer.succeed(
  PlatformConnectionSource.PlatformConnectionSource,
  PlatformConnectionSource.PlatformConnectionSource.of({
    registrations: Stream.fromEffect(
      Effect.tryPromise({
        try: resolveInitialPrimaryEnvironmentDescriptor,
        catch: (cause) => cause,
      }).pipe(
        Effect.tapError((error) =>
          Effect.logWarning("Could not read HeyDex's environment descriptor.", { error }),
        ),
        Effect.retry(DESCRIPTOR_RETRY),
        // The retry never stops, so no failure reaches here.
        Effect.orDie,
        Effect.map((descriptor): ReadonlyArray<PrimaryConnectionRegistration> => [
          new PrimaryConnectionRegistration({
            target: new PrimaryConnectionTarget({
              environmentId: descriptor.environmentId,
              label: descriptor.label,
              httpBaseUrl: primaryHttpBaseUrl(),
            }),
          }),
        ]),
      ),
    ),
  }),
);

const layerEnvironmentOwnedDataCleanup = Layer.succeed(
  Persistence.EnvironmentOwnedDataCleanup,
  Persistence.EnvironmentOwnedDataCleanup.of({
    clear: (environmentId) =>
      Effect.sync(() => {
        clearComposerDraftsEnvironment(environmentId);
      }),
  }),
);

const layerRpcRequestObserver = Layer.succeed(
  EnvironmentRpcRequestObserver,
  EnvironmentRpcRequestObserver.of({
    observe: ({ environmentId, method }) =>
      Effect.sync(() => {
        nextObservedRpcRequestId += 1;
        const requestId = `${environmentId}:${nextObservedRpcRequestId}`;
        trackRpcRequestSent(requestId, method, `${method} · ${environmentId}`);
        return Effect.sync(() => {
          acknowledgeRpcRequest(requestId);
        });
      }),
  }),
);

type ConnectionPlatformLayerSource =
  | typeof ConnectionStorage.layer
  | typeof layerPlatformConnectionSource
  | typeof layerEnvironmentOwnedDataCleanup
  | typeof layerRpcRequestObserver
  | typeof layerRuntimeConnector;

export const layer: Layer.Layer<
  Layer.Success<ConnectionPlatformLayerSource>,
  Layer.Error<ConnectionPlatformLayerSource>,
  Layer.Services<ConnectionPlatformLayerSource>
> = Layer.mergeAll(
  ConnectionStorage.layer,
  layerPlatformConnectionSource,
  layerEnvironmentOwnedDataCleanup,
  layerRpcRequestObserver,
  layerRuntimeConnector,
);
